import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, ClickAwayListener, Collapse, List, ListItemButton, ListItemIcon, ListItemText, Popover, Typography } from '@mui/material';

// project imports
import NavItem from '../NavItem';
import MiniNavTooltip from '../MiniNavTooltip';
import { useSelector } from '@/store';
import useConfig from '@/hooks/useConfig';

// assets
import { IconChevronDown, IconChevronRight, IconChevronUp } from '@tabler/icons-react';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { NavItemType } from '@/types';

// ==============================|| SIDEBAR MENU LIST COLLAPSE ITEMS ||============================== //

interface NavCollapseProps {
    menu: NavItemType;
    level: number;
    parentId: string;
}

const NavCollapse = ({ menu, level, parentId }: NavCollapseProps) => {
    const theme = useTheme();
    const { borderRadius } = useConfig();
    const { drawerOpen } = useSelector((state) => state.menu);

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<string | null | undefined>(null);
    const [openMini, setOpenMini] = useState(false);
    const anchorRef = useRef<HTMLDivElement | null>(null);

    const handleClickMini = (event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLDivElement, MouseEvent> | undefined) => {
        if (drawerOpen) {
            setOpen(!open);
            setSelected(!selected ? menu.id : null);
        } else {
            event?.stopPropagation();
            if (openMini) {
                handleClosePopper();
                return;
            }
            setOpenMini(true);
        }
    };

    const handleClosePopper = () => {
        setOpen(false);
        setSelected(null);
        setOpenMini(false);
    };

    const handleCloseMiniOnClickAway = (event: MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
            return;
        }
        handleClosePopper();
    };
    const { pathname } = useLocation();

    const checkOpenForParent = (child: NavItemType[], id: string) => {
        child.forEach((item: NavItemType) => {
            if (item.url === pathname) {
                setOpen(true);
                setSelected(id);
            }
        });
    };

    // menu collapse for sub-levels
    useEffect(() => {
        setOpen(false);
        setSelected(null);
        if (menu.children) {
            menu.children.forEach((item: NavItemType) => {
                if (item.children?.length) {
                    checkOpenForParent(item.children, menu.id!);
                }
                if (pathname && pathname.includes('product-details')) {
                    if (item.url && item.url.includes('product-details')) {
                        setSelected(menu.id);
                        setOpen(true);
                    }
                }
                if (item.url === pathname) {
                    setSelected(menu.id);
                    setOpen(true);
                }
            });
        }
    }, [pathname]);

    // menu collapse & item
    const menus = menu.children?.map((item) => {
        switch (item.type) {
            case 'collapse':
                return <NavCollapse key={item.id} menu={item} level={level + 1} parentId={parentId} />;
            case 'item':
                return <NavItem key={item.id} item={item} level={level + 1} parentId={parentId} />;
            default:
                return (
                    <Typography key={item.id} variant="h6" color="error" align="center">
                        Menu Items Error
                    </Typography>
                );
        }
    });

    const isSelected = selected === menu.id;

    const Icon = menu.icon!;
    const menuIcon = menu.icon ? (
        <Icon
            strokeWidth={1.5}
            size={drawerOpen ? '20px' : '24px'}
            style={{ color: isSelected ? theme.palette.secondary.main : theme.palette.text.primary }}
        />
    ) : (
        <FiberManualRecordIcon
            sx={{
                color: isSelected ? theme.palette.secondary.main : theme.palette.text.primary,
                width: isSelected ? 8 : 6,
                height: isSelected ? 8 : 6
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    const collapseIcon = drawerOpen ? (
        <IconChevronUp stroke={1.5} size="16px" style={{ marginTop: 'auto', marginBottom: 'auto' }} />
    ) : (
        <IconChevronRight stroke={1.5} size="16px" style={{ marginTop: 'auto', marginBottom: 'auto' }} />
    );

    const textColor = theme.palette.mode === 'dark' ? 'grey.400' : 'text.primary';
    const iconSelectedColor = theme.palette.mode === 'dark' && drawerOpen ? 'text.primary' : 'secondary.main';
    const showMiniLabel = !drawerOpen && level === 1;
    const miniAriaLabel = showMiniLabel
        ? [menu.title, menu.caption].filter((value): value is string => typeof value === 'string' && value.length > 0).join('. ')
        : undefined;

    const listItem = (
        <ListItemButton
            sx={{
                zIndex: 1201,
                borderRadius: `${borderRadius}px`,
                mb: 0.5,
                pl: drawerOpen ? `${level * 24}px` : 1.25,
                ...(drawerOpen &&
                    level === 1 &&
                    theme.palette.mode !== 'dark' && {
                        '&:hover': {
                            background: theme.palette.secondary.light
                        },
                        '&.Mui-selected': {
                            background: theme.palette.secondary.light,
                            color: iconSelectedColor,
                            '&:hover': {
                                color: iconSelectedColor,
                                background: theme.palette.secondary.light
                            }
                        }
                    }),
                ...((!drawerOpen || level !== 1) && {
                    py: level === 1 ? 0 : 1,
                    '&:hover': {
                        bgcolor: 'transparent'
                    },
                    '&.Mui-selected': {
                        '&:hover': {
                            bgcolor: 'transparent'
                        },
                        bgcolor: 'transparent'
                    }
                })
            }}
            selected={isSelected}
            onClick={handleClickMini}
            ref={anchorRef}
            aria-label={miniAriaLabel || undefined}
        >
            {menuIcon && (
                <ListItemIcon
                    sx={{
                        minWidth: level === 1 ? 36 : 18,
                        color: isSelected ? iconSelectedColor : textColor,
                        ...(!drawerOpen &&
                            level === 1 && {
                                borderRadius: `${borderRadius}px`,
                                width: 46,
                                height: 46,
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': {
                                    bgcolor: theme.palette.mode === 'dark' ? theme.palette.secondary.main + 25 : 'secondary.light'
                                },
                                ...(isSelected && {
                                    bgcolor: theme.palette.mode === 'dark' ? theme.palette.secondary.main + 25 : 'secondary.light',
                                    '&:hover': {
                                        bgcolor: theme.palette.mode === 'dark' ? theme.palette.secondary.main + 30 : 'secondary.light'
                                    }
                                })
                            })
                    }}
                >
                    {menuIcon}
                </ListItemIcon>
            )}
            {(drawerOpen || (!drawerOpen && level !== 1)) && (
                <ListItemText
                    primary={
                        <Typography variant={isSelected ? 'h5' : 'body1'} color="inherit" sx={{ my: 'auto' }}>
                            {menu.title}
                        </Typography>
                    }
                    secondary={
                        menu.caption && (
                            <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption, display: 'block' }} gutterBottom>
                                {menu.caption}
                            </Typography>
                        )
                    }
                />
            )}

            {openMini || open ? (
                collapseIcon
            ) : (
                <IconChevronDown stroke={1.5} size="16px" style={{ marginTop: 'auto', marginBottom: 'auto' }} />
            )}

            {!drawerOpen && (
                <Popover
                    open={openMini}
                    anchorEl={anchorRef.current}
                    onClose={handleClosePopper}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left'
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                ml: 0.5,
                                mt: 0,
                                overflow: 'hidden',
                                minWidth: 220,
                                borderRadius: 2,
                                boxShadow: theme.shadows[8],
                                backgroundImage: 'none',
                                '& .MuiList-root': {
                                    py: 0.5
                                },
                                '& .MuiListItemButton-root': {
                                    borderRadius: 1.5,
                                    mx: 0.5,
                                    my: 0.25,
                                    px: 1,
                                    py: 0.625,
                                    minHeight: 40
                                },
                                '& .MuiListItemIcon-root': {
                                    minWidth: 28,
                                    mr: 0.25
                                },
                                '& .MuiListItemText-root': {
                                    my: 0
                                },
                                '& .MuiListItemText-primary': {
                                    lineHeight: 1.25
                                }
                            }
                        }
                    }}
                >
                    <ClickAwayListener onClickAway={handleCloseMiniOnClickAway}>
                        <Box sx={{ p: 0.5 }}>{menus}</Box>
                    </ClickAwayListener>
                </Popover>
            )}
        </ListItemButton>
    );

    return (
        <>
            {showMiniLabel ? (
                <MiniNavTooltip title={menu.title} caption={menu.caption} forceClose={openMini}>
                    {listItem}
                </MiniNavTooltip>
            ) : (
                listItem
            )}
            {drawerOpen && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    {open && (
                        <List
                            component="div"
                            disablePadding
                            sx={{
                                position: 'relative',
                                '&:after': {
                                    content: "''",
                                    position: 'absolute',
                                    left: '32px',
                                    top: 0,
                                    height: '100%',
                                    width: '1px',
                                    opacity: theme.palette.mode === 'dark' ? 0.2 : 1,
                                    background: theme.palette.mode === 'dark' ? theme.palette.dark.light : theme.palette.primary.light
                                }
                            }}
                        >
                            {menus}
                        </List>
                    )}
                </Collapse>
            )}
        </>
    );
};

export default NavCollapse;
